import { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from './axiosInstance';

const toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2200, timerProgressBar: true });

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

// Loads the lab job queue, keeps it fresh (silent poll), and wraps every
// mutation so the admin page and the lab assistant dashboard behave the same.
// Every mutation returns true on success, false on failure (with a Swal error).
export default function useLabJobs({ pollMs = 20000 } = {}) {
  const [jobs, setJobs] = useState([]);
  const [viewer, setViewer] = useState({ currentStaffId: null, isLabAssistant: false, isManager: false, isSuperAdmin: false, canSeeAll: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const mounted = useRef(true);

  const load = useCallback(async (silent = false) => {
    try {
      const res = await axiosInstance.get('/admin/lab-jobs');
      if (!mounted.current) return;
      setJobs(res.data.jobs || []);
      setViewer({
        currentStaffId: res.data.currentStaffId || null,
        isLabAssistant: !!res.data.isLabAssistant,
        isManager: !!res.data.isManager,
        isSuperAdmin: !!res.data.isSuperAdmin,
        canSeeAll: !!res.data.canSeeAll,
      });
      setLoadError('');
    } catch (err) {
      if (!mounted.current) return;
      if (!silent) setLoadError(errorText(err, 'Failed to load the lab jobs.'));
    } finally {
      if (mounted.current && !silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load(false);
    const timer = setInterval(() => load(true), pollMs);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load, pollMs]);

  // Queue positions of other jobs shift whenever one leaves the line, so after
  // any mutation the whole list is refreshed instead of patching one job.
  const run = useCallback(async (request, { success, failure }) => {
    try {
      await request();
      if (success) toast.fire({ icon: 'success', title: success });
      await load(true);
      return true;
    } catch (err) {
      Swal.fire('Could not update', errorText(err, failure), 'error');
      await load(true);
      return false;
    }
  }, [load]);

  return {
    jobs,
    viewer,
    loading,
    loadError,
    reload: () => load(true),
    createJob: (payload) => run(() => axiosInstance.post('/admin/lab-jobs', payload), { success: 'Job added to the queue', failure: 'Failed to add the job.' }),
    updateJob: (jobId, payload) => run(() => axiosInstance.put(`/admin/lab-jobs/${jobId}`, payload), { success: 'Job updated', failure: 'Failed to update the job.' }),
    deleteJob: (jobId) => run(() => axiosInstance.delete(`/admin/lab-jobs/${jobId}`), { success: 'Job removed', failure: 'Failed to remove the job.' }),
    changeStatus: (jobId, status, note = '') => run(
      () => axiosInstance.put(`/admin/lab-jobs/${jobId}/status`, { status, note }),
      { success: 'Status updated', failure: 'Failed to change the status.' }
    ),
    addComment: (jobId, comment) => run(() => axiosInstance.post(`/admin/lab-jobs/${jobId}/comments`, { comment }), { failure: 'Failed to post the comment.' }),
    deleteComment: (jobId, commentId) => run(() => axiosInstance.delete(`/admin/lab-jobs/${jobId}/comments/${commentId}`), { failure: 'Failed to delete the comment.' }),
  };
}
